import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { TruCreateOrderService } from 'src/app/public-website/buyer/services/tru-create-order.service';
import { TruConfirmOrderService } from 'src/app/public-website/buyer/services/tru-confirm-order.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-tru',
  templateUrl: './tru.component.html',
  styleUrls: ['./tru.component.scss'],
})
export class TruComponent implements OnInit {
  isStep2: boolean = false;
  orderId: string = '';
  providerRefNum: string = '';
  data: Data;
  transactionDate: string = new Date().toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });

  constructor(
    private _formBuilder: FormBuilder,
    private createOrder: TruCreateOrderService,
    private confirmOrder: TruConfirmOrderService,
    private router: Router,
    private translate: TranslateService
  ) {}

  truForm = this._formBuilder.group({
    truId: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
  });

  truOtp = this._formBuilder.group({
    otp: ['', [Validators.required, Validators.minLength(4)]],
  });

  ngOnInit(): void {
    this.data = JSON.parse(localStorage.getItem('truData'));
    if (!this.data.productName) {
      this.data.productName = 'Direct Payment';
    }
    const savedData = JSON.parse(localStorage.getItem('truOrderData'));
    if (savedData) {
      if (savedData.productId === this.getCurrentProductId()) {
        Swal.fire({
          icon: 'info',
          text: `You have pending tru order #${savedData.orderId}. Do you wish to complete it?`,
          showCancelButton: true,
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
        }).then((result) => {
          if (result.isConfirmed) {
            this.isStep2 = true;
            this.orderId = savedData.orderId;
            this.providerRefNum = savedData.providerRefNum;
          } else {
            localStorage.removeItem('truOrderData');
            localStorage.setItem('truStep', '1');
            this.isStep2 = false;
          }
        });
      } else {
        return;
      }
    }
  }

  async createOrderRequest() {
    if (this.truForm.invalid) {
      return;
    }

    try {
      const orderBody = {
        userId: this.truForm.controls['truId'].value,
        productName: this.data.productName,
        totalAmount: this.data.totalAmount,
        itemPrice: this.data.totalAmount,
        paymentAttemptId: this.data.paymentAttemptId,
      };

      this.createOrder.setParams(orderBody);
      const response = await this.createOrder.make();
      if (response.success) {
        this.orderId = response.data.id;
        this.providerRefNum = response.data.providerRefNum;
        localStorage.setItem('truStep', '2');
        localStorage.setItem(
          'truOrderData',
          JSON.stringify({
            productId: this.getCurrentProductId(),
            orderId: this.orderId,
            providerRefNum: this.providerRefNum,
          })
        );
        this.isStep2 = true;
        this.router.navigate([], { replaceUrl: true });
      }
    } catch (e) {
      this.handleError(e);
    }
  }

  async confirmPayment() {
    try {
      const otp = this.truOtp.controls['otp'].value;

      const paymentBody = {
        orderId: this.orderId,
        orderToken: otp,
        refNumber: this.providerRefNum,
      };

      this.confirmOrder.setParams(paymentBody);
      const response = await this.confirmOrder.make();
      if (response.success) {
        localStorage.removeItem('truStep');
        localStorage.removeItem('truData');
        localStorage.removeItem('truOrderData');
        await Swal.fire({
          position: 'center',
          icon: 'success',
          text: this.translate.instant('PROVIDER.TRU.MESSAGESUCCESS'),
          showConfirmButton: false,
          showCancelButton: true,
        });
        this.redirectToCustomer();
      }
    } catch (e) {
      this.handleError(e);
    }
  }

  private getCurrentProductId(): number {
    return this.data?.productId;
  }

  private async handleError(error: any) {
    const errorMessage =
      error?.faultyRequest?.error?.message || 'Unexpected Error';
    let extractedMessage = 'An unexpected error occurred';

    if (errorMessage.startsWith('TRU') && errorMessage.endsWith('400')) {
      const parts = errorMessage.split(' _ ');
      if (parts.length >= 4) {
        extractedMessage = parts[2];
      }
    }

    await Swal.fire({
      icon: 'error',
      text: extractedMessage,
      confirmButtonText: 'OK',
    });
  }

  private redirectToCustomer() {
    const url = localStorage.getItem('customerRedirectUrl');
    if (url) {
      const customerRedirectUrl = new URL(url);
      customerRedirectUrl.searchParams.set('status', 'success');
      window.location.href = customerRedirectUrl.toString();
    } else {
      this.router.navigate(['/']);
    }
  }
}

type Data = {
  totalAmount: number;
  productId: number;
  productName: string;
  paymentAttemptId: number;
  businessName: string;
};
